import { WorkModel } from "../../factories/works/models/work.model";

const DAY = 24 * 60 * 60 * 1000;

export const startOfDay = (date: Date) => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

const endOfDay = (date: Date) => {
  const day = new Date(date);
  day.setHours(23, 59, 59, 999);
  return day;
};

/** Whole days from `from` to `to` (calendar days, local time). */
export const daysBetween = (from: Date, to: Date) =>
  Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY);

const toDate = (value?: string | Date | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// ---------------------------------------------------------------- dates

export type DatePreset =
  | "thisMonth"
  | "lastMonth"
  | "thisQuarter"
  | "thisYear"
  | "lastYear"
  | "last12Months"
  | "all";

export const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: "thisMonth", label: "This month" },
  { value: "lastMonth", label: "Last month" },
  { value: "thisQuarter", label: "This quarter" },
  { value: "thisYear", label: "This year" },
  { value: "lastYear", label: "Last year" },
  { value: "last12Months", label: "Last 12 months" },
  { value: "all", label: "All time" },
];

export type DateRange = { from: Date; to: Date } | null;

/** The date range of a preset; null = no limit ("All time"). */
export const presetRange = (
  preset: DatePreset,
  today: Date = new Date(),
): DateRange => {
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (preset) {
    case "thisMonth":
      return { from: new Date(y, m, 1), to: endOfDay(new Date(y, m + 1, 0)) };
    case "lastMonth":
      return { from: new Date(y, m - 1, 1), to: endOfDay(new Date(y, m, 0)) };
    case "thisQuarter": {
      const q = Math.floor(m / 3) * 3;
      return { from: new Date(y, q, 1), to: endOfDay(new Date(y, q + 3, 0)) };
    }
    case "thisYear":
      return { from: new Date(y, 0, 1), to: endOfDay(new Date(y, 11, 31)) };
    case "lastYear":
      return {
        from: new Date(y - 1, 0, 1),
        to: endOfDay(new Date(y - 1, 11, 31)),
      };
    case "last12Months":
      // Same as the Time page: the 12 full months before this one, plus this month.
      return { from: new Date(y, m - 12, 1), to: endOfDay(today) };
    default:
      return null;
  }
};

export const inRange = (
  value: string | Date | null | undefined,
  range: DateRange,
) => {
  const date = toDate(value);
  if (!date) return false;
  return !range || (date >= range.from && date <= range.to);
};

/** "yyyy-mm-dd" in local time (what the time/invoice filters expect). */
export const isoDay = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const monthKey = (date: Date) =>
  `${date.getFullYear()}-${date.getMonth()}`;

/** Month buckets covering the range (at most 24, newest last). */
export const monthsOf = (range: DateRange, dates: Date[]) => {
  let from = range?.from;
  let to = range?.to;
  if (!from || !to) {
    if (!dates.length) return [];
    from = new Date(Math.min(...dates.map((d) => d.getTime())));
    to = new Date(Math.max(...dates.map((d) => d.getTime())));
  }
  const last = new Date(to.getFullYear(), to.getMonth(), 1);
  const now = new Date();
  const cap = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = last > cap ? cap : last;
  const months: { key: string; label: string }[] = [];
  const cursor = new Date(from.getFullYear(), from.getMonth(), 1);
  while (cursor <= end) {
    const multiYear = from.getFullYear() !== end.getFullYear();
    months.push({
      key: monthKey(cursor),
      label:
        MONTHS[cursor.getMonth()] +
        (multiYear ? ` ${String(cursor.getFullYear()).slice(2)}` : ""),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months.slice(-24);
};

export const formatDate = (value?: string | Date | null) => {
  const date = toDate(value);
  if (!date) return "—";
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

export const formatMoney = (value: number) =>
  "$" + Math.round(value).toLocaleString("en-US");

export const formatHours = (hours: number) => {
  if (hours > 0 && hours < 0.05) return "<0.1 h";
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded.toLocaleString("en-US")} h`;
};

export const percent = (part: number, whole: number) =>
  whole > 0 ? Math.round((part / whole) * 100) : 0;

// ---------------------------------------------------------------- work

export type StatusGroup =
  | "Ready"
  | "In Progress"
  | "Waiting"
  | "Scheduled"
  | "Completed";

export const OPEN_GROUPS: StatusGroup[] = [
  "Ready",
  "In Progress",
  "Waiting",
  "Scheduled",
];

export const STATUS_COLORS: Record<StatusGroup, string> = {
  Ready: "#9AA3B2",
  "In Progress": "#3B6FE0",
  Waiting: "#E08A1E",
  Scheduled: "#1F2433",
  Completed: "#16A34A",
};

/** The main status group of a work item (its `jobStatus`). */
export const statusGroup = (
  work: Pick<WorkModel, "jobStatus">,
): StatusGroup => {
  const name = (work.jobStatus || "").trim().toLowerCase();
  if (name.startsWith("in progress") || name === "inprogress")
    return "In Progress";
  if (name.startsWith("waiting")) return "Waiting";
  if (name.startsWith("scheduled")) return "Scheduled";
  if (name.startsWith("completed")) return "Completed";
  return "Ready";
};

export const isOverdue = (
  work: Pick<WorkModel, "dueDate">,
  today: Date = new Date(),
) => {
  const due = toDate(work.dueDate);
  return !!due && due < startOfDay(today);
};

export const trackedHours = (work: Pick<WorkModel, "totalDuration">) =>
  (work.totalDuration || 0) / 3600;

export type Person = { id: string; name: string; color?: string };

export const NOT_ASSIGNED: Person = {
  id: "",
  name: "Not assigned (role only)",
};

export const peopleOf = (work: WorkModel): Person[] =>
  work.assigneeList?.length
    ? work.assigneeList.map((a) => ({
        id: a.assignedUserId,
        name: `${a.firstName || ""} ${a.lastName || ""}`.trim() || "Unknown",
        color: a.color || undefined,
      }))
    : [NOT_ASSIGNED];

export type WorkloadRow = Person & {
  counts: Record<StatusGroup, number>;
  total: number;
  overdue: number;
  hoursLeft: number;
};

/** Open work per team member. Work with two assignees counts for both. */
export const buildWorkload = (works: WorkModel[], today: Date = new Date()) => {
  const rows = new Map<string, WorkloadRow>();
  works.forEach((work) => {
    const group = statusGroup(work);
    if (group === "Completed") return;
    const left = Math.max(0, (work.budgetedTime || 0) - trackedHours(work));
    peopleOf(work).forEach((person) => {
      const row =
        rows.get(person.id) ??
        ({
          ...person,
          counts: {
            Ready: 0,
            "In Progress": 0,
            Waiting: 0,
            Scheduled: 0,
            Completed: 0,
          },
          total: 0,
          overdue: 0,
          hoursLeft: 0,
        } as WorkloadRow);
      row.counts[group] += 1;
      row.total += 1;
      if (isOverdue(work, today)) row.overdue += 1;
      row.hoursLeft += left;
      rows.set(person.id, row);
    });
  });
  return Array.from(rows.values()).sort(
    (a, b) => Number(a.id === "") - Number(b.id === "") || b.total - a.total,
  );
};

/** Overdue, due this week (to Sunday) and due in the next 30 days. */
export const splitDeadlines = (
  works: WorkModel[],
  today: Date = new Date(),
) => {
  const todayStart = startOfDay(today);
  const sunday = new Date(todayStart);
  sunday.setDate(sunday.getDate() + ((7 - sunday.getDay()) % 7));
  const weekEnd = endOfDay(sunday);
  const monthEnd = endOfDay(new Date(todayStart.getTime() + 30 * DAY));
  const open = works
    .filter((w) => statusGroup(w) !== "Completed")
    .map((work) => ({ work, due: toDate(work.dueDate) }))
    .filter((x): x is { work: WorkModel; due: Date } => !!x.due)
    .sort((a, b) => a.due.getTime() - b.due.getTime());
  const pick = (keep: (due: Date) => boolean) =>
    open.filter((x) => keep(x.due)).map((x) => x.work);
  return {
    overdue: pick((due) => due < todayStart),
    week: pick((due) => due >= todayStart && due <= weekEnd),
    month: pick((due) => due > weekEnd && due <= monthEnd),
  };
};

/** "3 days late", "Today", "4 days left". */
export const dueLabel = (due: string, today: Date = new Date()) => {
  const days = daysBetween(today, new Date(due));
  if (days === 0) return "Today";
  if (days < 0) return `${-days} day${days === -1 ? "" : "s"} late`;
  return `${days} day${days === 1 ? "" : "s"} left`;
};

/**
 * Completed work: the completion date is when the status last changed
 * (`statusUpdatedAt`), on time = completed on or before the due date.
 */
export const completedStats = (works: WorkModel[], range: DateRange) => {
  const done = works
    .map((work) => ({ work, at: toDate(work.statusUpdatedAt) }))
    .filter(
      (x) =>
        x.at && statusGroup(x.work) === "Completed" && inRange(x.at, range),
    ) as {
    work: WorkModel;
    at: Date;
  }[];
  const onTime = (x: { work: WorkModel; at: Date }) => {
    const due = toDate(x.work.dueDate);
    return !due || x.at <= endOfDay(due);
  };
  const daysToFinish = (x: { work: WorkModel; at: Date }) => {
    const start = toDate(x.work.startDate);
    return start ? Math.max(0, daysBetween(start, x.at)) : null;
  };
  const average = (items: typeof done) => {
    const days = items.map(daysToFinish).filter((d): d is number => d !== null);
    return days.length ? days.reduce((s, d) => s + d, 0) / days.length : null;
  };

  const months = monthsOf(
    range,
    done.map((x) => x.at),
  ).map((month) => {
    const items = done.filter((x) => monthKey(x.at) === month.key);
    const late = items.filter((x) => !onTime(x)).length;
    return { ...month, total: items.length, late, onTime: items.length - late };
  });

  const groupBy = (
    keyOf: (work: WorkModel) => { id: string; name: string }[],
  ) => {
    const groups = new Map<
      string,
      { id: string; name: string; items: typeof done }
    >();
    done.forEach((x) =>
      keyOf(x.work).forEach((key) => {
        const group = groups.get(key.id) ?? { ...key, items: [] };
        group.items.push(x);
        groups.set(key.id, group);
      }),
    );
    return Array.from(groups.values())
      .map((g) => {
        const late = g.items.filter((x) => !onTime(x)).length;
        return {
          id: g.id,
          name: g.name,
          completed: g.items.length,
          late,
          onTimePct: percent(g.items.length - late, g.items.length),
          avgDays: average(g.items),
        };
      })
      .sort((a, b) => b.completed - a.completed);
  };

  const late = done.filter((x) => !onTime(x)).length;
  return {
    total: done.length,
    /** Completed work with no status-change date (can't be placed in a period). */
    noDate: works.filter(
      (w) => statusGroup(w) === "Completed" && !toDate(w.statusUpdatedAt),
    ).length,
    late,
    onTimePct: percent(done.length - late, done.length),
    avgDays: average(done),
    months,
    byPerson: groupBy((w) => peopleOf(w)),
    byWorkType: groupBy((w) => [
      { id: w.workTypeId || "", name: w.workType || "No work type" },
    ]),
  };
};

/** Days since the status last changed; null when unknown. */
export const daysInStatus = (work: WorkModel, today: Date = new Date()) => {
  const since = toDate(work.statusUpdatedAt);
  return since ? Math.max(0, daysBetween(since, today)) : null;
};

export const timeInStatus = (
  works: WorkModel[],
  minDays: number,
  today: Date = new Date(),
) => {
  const all = works
    .filter((w) => statusGroup(w) !== "Completed")
    .map((work) => ({ work, days: daysInStatus(work, today) }));
  const open = all.filter(
    (x): x is { work: WorkModel; days: number } => x.days !== null,
  );
  const avg = (items: typeof open) =>
    items.length ? items.reduce((s, x) => s + x.days, 0) / items.length : 0;
  const byStatus = OPEN_GROUPS.map((group) => {
    const items = open.filter((x) => statusGroup(x.work) === group);
    return { group, count: items.length, avgDays: avg(items) };
  }).filter((x) => x.count > 0);
  const slowest = [...byStatus].sort((a, b) => b.avgDays - a.avgDays)[0];
  const rows = open
    .filter((x) => x.days >= minDays)
    .sort((a, b) => b.days - a.days);
  return {
    rows,
    avgDays: avg(open),
    longest: [...open].sort((a, b) => b.days - a.days)[0] ?? null,
    byStatus,
    slowest: slowest ?? null,
    /** Open work whose status-change date is unknown (left out above). */
    noDate: all.length - open.length,
  };
};

export const budgetRows = (works: WorkModel[]) =>
  works
    .filter((w) => (w.budgetedTime || 0) > 0)
    .map((work) => {
      const budget = work.budgetedTime || 0;
      const tracked = trackedHours(work);
      return {
        work,
        budget,
        tracked,
        diff: tracked - budget,
        pct: percent(tracked, budget),
      };
    })
    .sort((a, b) => b.pct - a.pct);

export const budgetByWorkType = (rows: ReturnType<typeof budgetRows>) => {
  const groups = new Map<
    string,
    { name: string; budget: number; tracked: number }
  >();
  rows.forEach((r) => {
    const key = r.work.workTypeId || "";
    const group = groups.get(key) ?? {
      name: r.work.workType || "No work type",
      budget: 0,
      tracked: 0,
    };
    group.budget += r.budget;
    group.tracked += r.tracked;
    groups.set(key, group);
  });
  return Array.from(groups.values()).sort((a, b) => b.budget - a.budget);
};

// ---------------------------------------------------------------- time

export type TimeEntry = {
  id: string;
  date: string | Date;
  duration: number | null;
  isBilled?: boolean;
  /** false = not charged to the client; missing (older data) = billable. */
  isBillable?: boolean;
  assignedUserId: string;
  assignedUser?: {
    hourlyRate?: number;
    userProfile?: { firstName?: string; lastName?: string; color?: string };
  } | null;
  customerId: string | null;
  contact?: { firstName?: string; lastName?: string } | null;
  businessContact?: { companyName?: string } | null;
  workTypeId?: string | null;
  /** filter-time sends the whole work type object here. */
  workType?: { name?: string | null } | string | null;
  work?: { name?: string; service?: { name?: string } | null } | null;
};

export const entryHours = (entry: TimeEntry) => (entry.duration || 0) / 3600;

/** Billable unless marked non-billable (time already invoiced counts as billable). */
export const isBillableEntry = (entry: TimeEntry) =>
  entry.isBilled === true || entry.isBillable !== false;

/** Value at the person's current hourly rate (as on the Time page). */
export const entryValue = (entry: TimeEntry) =>
  entryHours(entry) * (entry.assignedUser?.hourlyRate || 0);

export const entryClient = (entry: TimeEntry) => {
  if (entry.contact) {
    return (
      `${entry.contact.firstName || ""} ${
        entry.contact.lastName || ""
      }`.trim() || "Unknown client"
    );
  }
  return entry.businessContact?.companyName || "No client";
};

/** Group key for a client; time whose client record wasn't found groups by name. */
export const clientKey = (entry: TimeEntry) =>
  entry.customerId || `name:${entryClient(entry)}`;

export const entryPerson = (entry: TimeEntry) =>
  `${entry.assignedUser?.userProfile?.firstName || ""} ${
    entry.assignedUser?.userProfile?.lastName || ""
  }`.trim() || "Unknown";

export const entryWorkType = (entry: TimeEntry) =>
  (typeof entry.workType === "string"
    ? entry.workType
    : entry.workType?.name) ||
  entry.work?.service?.name ||
  "No work type";

export type TimeGroupBy = "person" | "client" | "workType";

export const groupTime = (entries: TimeEntry[], by: TimeGroupBy) => {
  const groups = new Map<
    string,
    {
      id: string;
      name: string;
      hours: number;
      billable: number;
      nonBillable: number;
      billed: number;
      /** Billable time not on an invoice yet. */
      unbilled: number;
      /** Value of the billable time. */
      value: number;
    }
  >();
  entries.forEach((entry) => {
    const id =
      by === "person"
        ? entry.assignedUserId
        : by === "client"
        ? clientKey(entry)
        : entry.workTypeId || entryWorkType(entry);
    const name =
      by === "person"
        ? entryPerson(entry)
        : by === "client"
        ? entryClient(entry)
        : entryWorkType(entry);
    const group = groups.get(id) ?? {
      id,
      name,
      hours: 0,
      billable: 0,
      nonBillable: 0,
      billed: 0,
      unbilled: 0,
      value: 0,
    };
    const hours = entryHours(entry);
    group.hours += hours;
    if (!isBillableEntry(entry)) {
      group.nonBillable += hours;
    } else {
      group.billable += hours;
      group.value += entryValue(entry);
      if (entry.isBilled) group.billed += hours;
      else group.unbilled += hours;
    }
    groups.set(id, group);
  });
  return Array.from(groups.values()).sort((a, b) => b.hours - a.hours);
};

export const unbilledByClient = (
  entries: TimeEntry[],
  today: Date = new Date(),
) => {
  const groups = new Map<
    string,
    {
      key: string;
      customerId: string;
      name: string;
      entries: number;
      hours: number;
      value: number;
      oldest: Date | null;
    }
  >();
  entries
    .filter((e) => !e.isBilled && isBillableEntry(e))
    .forEach((entry) => {
      const id = clientKey(entry);
      const group = groups.get(id) ?? {
        key: id,
        /** Empty when the client record wasn't found (no "Create invoice"). */
        customerId: entry.customerId || "",
        name: entryClient(entry),
        entries: 0,
        hours: 0,
        value: 0,
        oldest: null,
      };
      group.entries += 1;
      group.hours += entryHours(entry);
      group.value += entryValue(entry);
      const date = toDate(entry.date);
      if (date && (!group.oldest || date < group.oldest)) group.oldest = date;
      groups.set(id, group);
    });
  return Array.from(groups.values())
    .map((g) => ({
      ...g,
      ageDays: g.oldest ? daysBetween(g.oldest, today) : null,
    }))
    .sort((a, b) => b.value - a.value || b.hours - a.hours);
};

// ---------------------------------------------------------------- billing

export type InvoiceLike = {
  invoiceId?: string;
  id?: string;
  invoiceNumber?: string;
  invoiceDate: string;
  dueDate: string;
  total: number;
  balance: number;
  customerId: string;
  customerName: string;
  userId?: string;
  userFirstName?: string;
  userLastName?: string;
};

export const AGING_BUCKETS = ["notDue", "d0", "d31", "d61", "d91"] as const;
export type AgingBucket = typeof AGING_BUCKETS[number];

export const AGING_LABELS: Record<AgingBucket, string> = {
  notDue: "Not due yet",
  d0: "0–30 days",
  d31: "31–60 days",
  d61: "61–90 days",
  d91: "Over 90 days",
};

export type AgeBy = "invoiceDate" | "dueDate";

export const agingBucket = (
  invoice: InvoiceLike,
  ageBy: AgeBy,
  today: Date = new Date(),
): AgingBucket => {
  const from = toDate(
    ageBy === "dueDate" ? invoice.dueDate : invoice.invoiceDate,
  );
  if (!from) return "d0";
  const days = daysBetween(from, today);
  if (days < 0 || (ageBy === "dueDate" && days === 0))
    return ageBy === "dueDate" ? "notDue" : "d0";
  if (days <= 30) return "d0";
  if (days <= 60) return "d31";
  if (days <= 90) return "d61";
  return "d91";
};

export const emptyBuckets = () =>
  Object.fromEntries(AGING_BUCKETS.map((b) => [b, 0])) as Record<
    AgingBucket,
    number
  >;

export const agingByClient = (
  invoices: InvoiceLike[],
  ageBy: AgeBy,
  today: Date = new Date(),
) => {
  const unpaid = invoices.filter((i) => (i.balance || 0) > 0.004);
  const totals = emptyBuckets();
  const groups = new Map<
    string,
    {
      customerId: string;
      name: string;
      buckets: Record<AgingBucket, number>;
      total: number;
      count: number;
    }
  >();
  unpaid.forEach((invoice) => {
    const bucket = agingBucket(invoice, ageBy, today);
    totals[bucket] += invoice.balance;
    const group = groups.get(invoice.customerId) ?? {
      customerId: invoice.customerId,
      name: invoice.customerName,
      buckets: emptyBuckets(),
      total: 0,
      count: 0,
    };
    group.buckets[bucket] += invoice.balance;
    group.total += invoice.balance;
    group.count += 1;
    groups.set(invoice.customerId, group);
  });
  const total = unpaid.reduce((s, i) => s + i.balance, 0);
  return {
    total,
    count: unpaid.length,
    totals,
    rows: Array.from(groups.values()).sort((a, b) => b.total - a.total),
  };
};

export type RevenueLine = { invoiceId: string; name: string; amount: number };

export const revenueByMonth = (invoices: InvoiceLike[], range: DateRange) => {
  const dated = invoices
    .map((i) => ({ i, at: toDate(i.invoiceDate) }))
    .filter((x) => x.at) as {
    i: InvoiceLike;
    at: Date;
  }[];
  return monthsOf(
    range,
    dated.map((x) => x.at),
  ).map((month) => ({
    ...month,
    total: dated
      .filter((x) => monthKey(x.at) === month.key)
      .reduce((s, x) => s + (x.i.total || 0), 0),
  }));
};

export const revenueGroups = (
  invoices: InvoiceLike[],
  keyOf: (invoice: InvoiceLike) => { id: string; name: string },
) => {
  const groups = new Map<
    string,
    { id: string; name: string; invoiced: number; unpaid: number }
  >();
  invoices.forEach((invoice) => {
    const key = keyOf(invoice);
    const group = groups.get(key.id) ?? { ...key, invoiced: 0, unpaid: 0 };
    group.invoiced += invoice.total || 0;
    group.unpaid += Math.max(0, invoice.balance || 0);
    groups.set(key.id, group);
  });
  return Array.from(groups.values())
    .map((g) => ({ ...g, paid: g.invoiced - g.unpaid }))
    .sort((a, b) => b.invoiced - a.invoiced);
};

export const NOT_SPLIT = "Not split into services";

/**
 * Revenue per service. Each invoice's total (with tax and discount) is shared
 * between its services by line amount, so the services add up to the same
 * total as the other views. Invoices without lines show as NOT_SPLIT.
 */
export const serviceGroups = (
  invoices: InvoiceLike[],
  lines: RevenueLine[],
) => {
  const linesByInvoice = new Map<string, RevenueLine[]>();
  lines.forEach((line) =>
    linesByInvoice.set(line.invoiceId, [
      ...(linesByInvoice.get(line.invoiceId) ?? []),
      line,
    ]),
  );
  const groups = new Map<
    string,
    { id: string; name: string; invoiced: number }
  >();
  const add = (name: string, amount: number) => {
    const group = groups.get(name) ?? { id: name, name, invoiced: 0 };
    group.invoiced += amount;
    groups.set(name, group);
  };
  invoices.forEach((invoice) => {
    const total = invoice.total || 0;
    const own = linesByInvoice.get(invoice.invoiceId || invoice.id || "") ?? [];
    const sum = own.reduce((s, l) => s + Math.max(0, l.amount || 0), 0);
    if (!sum) {
      if (total) add(NOT_SPLIT, total);
      return;
    }
    own.forEach((line) =>
      add(line.name, (total * Math.max(0, line.amount || 0)) / sum),
    );
  });
  return Array.from(groups.values())
    .filter((g) => Math.abs(g.invoiced) >= 0.005)
    .sort((a, b) => b.invoiced - a.invoiced);
};

/**
 * Where a team member's name in a report goes (QA batch 12): their open work
 * on the Work page (/works/all?userId=, firm-wide access), or My Work for
 * yourself; null = no link (no access, or "Unassigned").
 */
export const personWorkLink = (
  personId: string | null | undefined,
  access: { firmWide: boolean; userId?: string | null },
): string | null => {
  if (!personId) return null;
  if (access.firmWide)
    return `/works/all?userId=${encodeURIComponent(personId)}`;
  return personId === access.userId ? "/works/my" : null;
};
